import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout, tokenRefreshed } from '../slices/authSlice';
import { sessionFromRefresh } from './authHelpers';
import { cleanObject } from '../../utilities/object';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://backend.alphacapitalgroup.uk';

/** The tick service is its own host, so its endpoints pass an absolute URL. */
export const TICKDATA_BASE_URL =
  process.env.REACT_APP_TICKDATA_BASE_URL || 'https://tickdata.alphacapitalgroup.uk';

// `getSharedCertificate` is here for the second reason as much as the first:
// a signed-in visitor opening someone else's private certificate must not be
// logged out by its 401.
const PUBLIC_ENDPOINTS = new Set([
  'login',
  'signup',
  'googleAuth',
  'verify2fa',
  // Renewal carries the refresh token in its body and no access token — see
  // `refreshQuery` below for why the header must stay off.
  'renewSession',
  'getSharedCertificate',
  'getPublicAccount',
  // The sign-in slider's country payout figures: public, and an expired
  // token left in the store would only get the request refused.
  'getCountryPayout',
  // Forgot password, from the signed-out reset page.
  'requestPasswordReset',
]);

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint }) => {
    if (PUBLIC_ENDPOINTS.has(endpoint)) return headers;
    const { token } = getState().auth;
    if (token) headers.set('authorization', `Bearer ${token}`);
    return headers;
  },
});
const SELF_HANDLED_401 = new Set(['renewSession', 'getCountryPayout']);

/**
 * The refresh call, without the `Authorization` header (TI review E7). The
 * backend checks an access token before it reads the body, so a refresh sent
 * with the expired token it is meant to replace is refused as
 * `401 invalid-auth-token` — every renewal failed and a remembered session was
 * signed out the first time its hour ran out. Main's client never sent the
 * header on this call either.
 */
const refreshQuery = fetchBaseQuery({ baseUrl: API_BASE_URL, credentials: 'include' });

/**
 * The one refresh in flight, so a page that fires eight requests at once and
 * gets eight 401s spends one refresh token rather than eight — with a rotating
 * endpoint the other seven would be spending a token that the first call had
 * already invalidated, and the whole page would land on the sign-in form.
 */
let refreshing = null;

/**
 * Renew the access token through `POST /get/refreshtoken/`. True when the store
 * is holding a fresh one afterwards.
 *
 * Only for a session that asked to be remembered: "Remember for 30 days" on the
 * sign-in form is what buys the renewal, and without it the session is meant to
 * end when its token does rather than be kept alive behind the trader's back.
 *
 * Called through `baseQuery` rather than an `authApi` endpoint on purpose: that
 * file imports `apiSlice`, so reaching back the other way would close the cycle
 * — and this call must not pass through the 401 handling below, which would
 * recurse into itself.
 */
const refreshSession = (api, extraOptions) => {
  const { refreshToken, remember } = api.getState().auth;
  if (!remember || !refreshToken) return Promise.resolve(false);

  refreshing ??= (async () => {
    const { data } = await refreshQuery(
      { url: '/get/refreshtoken/', method: 'POST', body: { refresh_token: refreshToken } },
      api,
      extraOptions,
    );

    const session = sessionFromRefresh(data);
    if (!session) return false;
    api.dispatch(tokenRefreshed(session));
    return true;
  })()
    // A refusal is an expired or revoked refresh token, not a crash — the
    // caller reads it as "could not renew" and signs the session out.
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
};

/**
 * Every endpoint's query params go through `cleanObject` here rather than each
 * one guarding its own spread — an unset filter, an empty search box or a
 * cleared date is left off the URL instead of sent as `?search=`.
 *
 * A 401 is answered with one refresh and one retry before the session is given
 * up on: the access token only lives an hour, and the trader shouldn't be sent
 * back to the sign-in form every time it turns over mid-session.
 */
const baseQueryWithReauth = async (args, api, extraOptions) => {
  const request = args?.params ? { ...args, params: cleanObject(args.params) } : args;
  const result = await baseQuery(request, api, extraOptions);
  if (
    result.error?.status === 401 &&
    !PUBLIC_ENDPOINTS.has(api.endpoint) &&
    !SELF_HANDLED_401.has(api.endpoint)
  ) {
    // `prepareHeaders` reads the token off the store when the request is built,
    // so the retry carries the new one without being told about it.
    if (await refreshSession(api, extraOptions)) return baseQuery(request, api, extraOptions);
    api.dispatch(deauthenticate());
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Auth', 'Account', 'Trade', 'Payout', 'Competition', 'Challenge', 'Certificate', 'Transaction', 'Kyc', 'PaymentVerification', 'Profile', 'Notification', 'Journal', 'Affiliate', 'ProductLaunch'],
  endpoints: () => ({}),
});

export const deauthenticate = () => (dispatch) => {
  dispatch(logout());
  dispatch(apiSlice.util.resetApiState());
};
